import React, { use, useEffect, useMemo, useState } from "react";
import { Search, Check, X, Building2, Eye, Loader2 } from "lucide-react";
import axios, { Axios } from "axios";
import { Modal, Input, Select, DatePicker } from "antd";
import AdminLayout from "../components/AdminLayout";
import { BASE_URL } from "../../../config/config";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

const AdminProperties = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
const [isRentDistributorOpen, setIsRentDistributorOpen] = useState(false);
const [selectedProperty, setSelectedProperty] = useState(null);
const [rentAmount, setRentAmount] = useState("");
const [selectedMonth, setSelectedMonth] = useState(null);
const [isRentSubmitting, setIsRentSubmitting] = useState(false);

const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
const [propertyToApprove, setPropertyToApprove] = useState(null);
const [vestingDays, setVestingDays] = useState("0");
const [isApproving, setIsApproving] = useState(false);
  /* ================= GET PROPERTIES ================= */

  const getProperties = async () => {
    try {
      setIsLoading(true);

      const token = localStorage.getItem("rwatoken");

      const response = await axios.get(`${BASE_URL}/admin/properties`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("Admin Properties Response:", response?.data);

      /*
        Handles both possible response formats:

        1. [
          {...},
          {...}
        ]

        2. {
          properties: [...]
        }
      */

      const data = Array.isArray(response?.data)
        ? response.data
        : response?.data?.properties || [];

      setProperties(data);
    } catch (error) {
      if(error?.status === 401){
        localStorage.removeItem("rwatoken");
        localStorage.removeItem("userRole");
        window.location.href = "/";
        navigate("/login", { replace: true });
      }
      console.log("Error fetching admin properties:", error);

      setProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProperties();
  }, []);

  /* ================= STATUS ================= */

  const getStatus = (property) => {
    const submissionStatus = property?.submission_status?.toLowerCase();
    let status;
    if (submissionStatus?.includes("pending")) {
      status = "Pending";
    } else if (submissionStatus?.includes("approved")) {
      status = "Approved";
    } else {
      status = "Rejected";
    }
    return status;
  };

  const getIsPublished = (property) => {
    if (property?.is_published === true) {
      return "Approved";
    } else {
      return "Pending";
    }
  };

  /* ================= FILTER ================= */

  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const status = getStatus(property);

      const matchesTab = activeTab === "All" || status === activeTab;

      const searchValue = search.toLowerCase().trim();

      const matchesSearch =
        !searchValue ||
        property?.name?.toLowerCase()?.includes(searchValue) ||
        property?.location?.toLowerCase()?.includes(searchValue) ||
        property?.property_type?.toLowerCase()?.includes(searchValue);

      return matchesTab && matchesSearch;
    });
  }, [properties, activeTab, search]);

  /* ================= COUNTS ================= */

  const pendingCount = properties.filter(
    (property) => getStatus(property) === "Pending",
  ).length;

  /* ================= ACTIONS ================= */

  const handleApprove = (property) => {
    setPropertyToApprove(property);
    setVestingDays(String(property?.vesting_days ?? 0));
    setIsApproveModalOpen(true);
  };

  const handleCloseApproveModal = () => {
    if (isApproving) return;
    setIsApproveModalOpen(false);
    setPropertyToApprove(null);
    setVestingDays("0");
  };

  const handleApproveSubmit = async () => {
    if (!propertyToApprove?.id) return;

    const token = localStorage.getItem("rwatoken");
    const payload = {
      decision: "approved",
      // Days newly-acquired tokens must wait before they can be resold or
      // transferred — enforced on-chain, not just a UI restriction. 0 means
      // no vesting (tokens are immediately transferable, same as before
      // this field existed).
      vesting_days: Number(vestingDays) || 0,
    };

    try {
      setIsApproving(true);

      const response = await axios.post(
        `${BASE_URL}/admin/properties/${propertyToApprove.id}/review`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response) {
        message.success("Property approved and tokenized.");
        handleCloseApproveModal();
        getProperties();
      }
    } catch (error) {
      console.log(error, "error");
      message.error(error?.response?.data?.details || "Something Went Wrong");
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (property) => {
    const token = localStorage.getItem("rwatoken");
    const payload = {
      decision: "rejected",
      reason: "Not Applicable",
      // token_address : "0x1111111111111111111111111111111111111111",
      // rent_distributor_address : "0x2222222222222222222222222222222222222222"
    };
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/properties/${property?.id}/review`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response) {
        message.success("Status Updated");
        getProperties();
      }
    } catch (error) {
      console.log(error, "error");
      message.error(error?.response?.data?.details || "Something Went Wrong");
    }
  };

  const handleView = async (property) => {
    const token = localStorage.getItem("rwatoken");
    const payload = {
      decision: "rejected",
      token_address: "0x1111111111111111111111111111111111111111",
      rent_distributor_address: "0x2222222222222222222222222222222222222222",
    };
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/properties/${property?.id}/review`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response) {
        message.success("Status Updated");
        getProperties();
      }
    } catch (error) {
      console.log(error, "error");
      message.error(error?.response?.data?.details || "Something Went Wrong");
    }

  };

  /* ================= RENDER ================= */
  const handleRentDistributor = (property) => {
  setSelectedProperty(property);
  setRentAmount("");
  setSelectedMonth(null);
  setIsRentDistributorOpen(true);
};
  const handleCloseRentDistributor = () => {
  setIsRentDistributorOpen(false);
  setSelectedProperty(null);
  setRentAmount("");
  setSelectedMonth(null);
};

 const handleRentDistributionSubmit = async () => {
  if (!rentAmount || !selectedMonth || !selectedProperty?.id) {
    return;
  }

  try {
    setIsRentSubmitting(true);

    const rentPeriod = selectedMonth.format("YYYY-MM");

    const payload = {
      total_amount_aed: Number(rentAmount),
      rent_period: rentPeriod,
    };

    console.log("Rent Deposit Payload:", payload);

    const response = await axios.post(
      `${BASE_URL}/admin/properties/${selectedProperty.id}/rent/deposit`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("rwatoken")}`,
          "Content-Type": "application/json",
        },
      }
    );
    message.success("Rent Distributed Successfully")
    console.log("Rent Deposit Response:", response?.data);

    handleCloseRentDistributor();

    // optional: refresh properties/list here
    // await getProperties();

  } catch (error) {
    console.log(
      "Rent deposit failed:",
      error?.response?.data || error
    );
    message.error(error?.response?.data?.detail || "Something Went Wrong")
  } finally {
    setIsRentSubmitting(false);
  }
};
  return (
    <AdminLayout title="Properties">
      {/* ================= HEADING ================= */}

      <div className="mb-5 flex items-start justify-between">
        <div>
          <h1 className="text-[20px] font-semibold text-[#142b3b]">
            Properties
          </h1>

          <p className="mt-1 text-[12px] text-[#84909b]">
            Review and manage all property listings.
          </p>
        </div>

        {pendingCount > 0 && (
          <div className="rounded-[7px] bg-[#fff2cf] px-3 py-2 text-[12px] font-medium text-[#b67800]">
            {pendingCount} Pending Review
          </div>
        )}
      </div>

      {/* ================= FILTERS ================= */}

      <div className="rounded-[9px] border border-[#e0e4e8] bg-white">
        <div className="flex flex-col gap-3 border-b border-[#edf0f2] p-3 sm:flex-row sm:items-center">
          {/* Tabs */}

          <div className="flex">
            {["All", "Pending", "Approved", "Rejected"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`rounded-[6px] px-3.5 py-2 text-[12px] ${
                  activeTab === tab
                    ? "bg-[#f2f5f8] font-semibold text-[#1d3444]"
                    : "text-[#71808c] hover:bg-[#f7f8f9]"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Search */}

          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa4ad]"
            />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, city or type..."
              className="h-[36px] w-full rounded-[7px] border border-[#e0e4e8] pl-9 pr-3 text-[12px] text-[#354858] outline-none placeholder:text-[#a0a8ae] focus:border-[#8caefb]"
            />
          </div>
        </div>

        {/* ================= TABLE ================= */}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] border-collapse">
            {/* HEADER */}

            <thead>
              <tr className="border-b border-[#e7eaed] bg-[#fafbfc]">
                {[
                  "Property",
                  "Type",
                  "Location",
                  "Valuation",
                  "Return",
                  "Funding",
                  "Status",
                  "IsPublished",
                  "Action",
                  "Rent Distribution"
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-4 py-3.5 text-left text-[13px] font-semibold text-[#71808c]"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            {/* BODY */}

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2
                        size={25}
                        className="animate-spin text-[#20485b]"
                      />

                      <p className="mt-3 text-[12px] text-[#89959e]">
                        Loading properties...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProperties?.map((property) => {
                  const status = getStatus(property);

                  const pubshlishedIS = getIsPublished(property);

                  const totalBlocks = Number(property?.total_tokens || 0);

                  const blocksSold = Number(property?.blocks_sold || 0);

                  const funding = Math.min(
                    Math.max(
                      Number(
                        property?.funding_pct ??
                          (totalBlocks ? (blocksSold / totalBlocks) * 100 : 0),
                      ),
                      0,
                    ),
                    100,
                  );

                  return (
                    <tr
                      key={property?.id}
                      className="border-b border-[#edf0f2] transition hover:bg-[#fafcfc] last:border-0"
                    >
                      {/* PROPERTY */}

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-[#eef2f1]">
                            {property?.image_paths?.[0] ? (
                              <img
                                src={`${BASE_URL}/uploads/${property.image_paths[0]}`}
                                alt={property?.name || "Property"}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Building2
                                size={17}
                                strokeWidth={1.5}
                                className="text-[#8b999e]"
                              />
                            )}
                          </div>

                          <div className="min-w-0">
                            <p className="max-w-[220px] truncate text-[13px] font-semibold text-[#173344]">
                              {property?.name || "Untitled Property"}
                            </p>

                            <p className="mt-1 text-[11px] text-[#9aa4ad]">
                              Owner ID: {property?.owner_id ?? "-"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* TYPE */}

                      <td className="px-4">
                        <span className="rounded-[5px] bg-[#f3f5f7] px-2.5 py-1.5 text-[11px] font-medium text-[#71808c]">
                          {property?.property_type || "-"}
                        </span>
                      </td>

                      {/* LOCATION */}

                      <td className="px-4 text-[12px] text-[#354858]">
                        {property?.location || "-"}
                      </td>

                      {/* VALUATION */}

                      <td className="px-4 text-[12px] font-semibold text-[#263a48]">
                        AED{" "}
                        {Number(
                          property?.property_value_aed || 0,
                        ).toLocaleString()}
                      </td>

                      {/* RETURN */}

                      <td className="px-4 text-[12px] font-semibold text-[#12964a]">
                        {Number(property?.gross_annual_yield_pct || 0).toFixed(
                          1,
                        )}
                        %
                      </td>

                      {/* FUNDING */}

                      <td className="px-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-[5px] w-[60px] overflow-hidden rounded-full bg-[#edf0f3]">
                            <div
                              className="h-full rounded-full bg-[#2c65ed]"
                              style={{
                                width: `${funding}%`,
                              }}
                            />
                          </div>

                          <span className="text-[11px] font-medium text-[#687782]">
                            {Math.round(funding)}%
                          </span>
                        </div>
                      </td>

                      {/* STATUS */}

                      <td className="px-4">
                        <StatusBadge status={status} />
                      </td>

                      <td className="px-4">
                        <IsPublished status={pubshlishedIS} />
                        {/* <StatusBadge
                            status={status}
                          /> */}
                      </td>

                      {/* ACTION */}

                      <td className="px-4">
                        {status === "Pending" ? (
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleApprove(property)}
                              className="flex items-center gap-1.5 rounded-[5px] bg-[#2c65ed] px-2.5 py-2 text-[10px] font-medium text-white transition hover:bg-[#2054ce]"
                            >
                              <Check size={11} />
                              Approve
                            </button>

                            <button
                              type="button"
                              onClick={() => handleReject(property)}
                              className="flex items-center gap-1.5 rounded-[5px] bg-[#ffe3e3] px-2.5 py-2 text-[10px] font-medium text-[#df3d3d] transition hover:bg-[#ffd4d4]"
                            >
                              <X size={11} />
                              Reject
                            </button>
                          </div>
                        ) : (
                          <StatusBadge status={status} />
                          // <button
                          //   type="button"
                          //   // onClick={() =>
                          //   //   handleView(
                          //   //     property
                          //   //   )
                          //   // }
                          //   className="flex items-center gap-1.5 rounded-[5px] bg-[#f3f5f7] px-2.5 py-2 text-[11px] font-medium text-[#586773] transition hover:bg-[#e9edef]"
                          // >

                          //   {/* <Eye size={12} />
                          //   View */}
                          // </button>
                        )}
                      </td>

                       <td className="px-4">
  {/* <button
    type="button"
    onClick={() => handleRentDistributor(property)}
    className="
      flex
      items-center
      gap-1.5
      rounded-[5px]
      bg-[rgb(46,105,7)]
      px-3
      py-2
      text-[13px]
      font-medium
      text-[#fff]
      transition
      hover:bg-[#295509]
    "
  >
   Distribute Rent
  </button> */}
  <button
  type="button"
  onClick={() => handleRentDistributor(property)}
  disabled={!Number(property?.blocks_sold)}
  className="
    flex
    items-center
    gap-1.5
    rounded-[5px]
    bg-[rgb(46,105,7)]
    px-3
    py-2
    text-[13px]
    font-medium
    text-white
    transition
    hover:bg-[#295509]
    disabled:cursor-not-allowed
    disabled:opacity-50
    disabled:hover:bg-[rgb(46,105,7)]
  "
>
  Distribute Rent
</button>
</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ================= EMPTY STATE ================= */}

        {!isLoading && filteredProperties.length === 0 && (
          <div className="flex min-h-[220px] flex-col items-center justify-center">
            <Building2 size={30} strokeWidth={1.3} className="text-[#b4bdc4]" />

            <p className="mt-3 text-[12px] font-medium text-[#89959e]">
              No properties found
            </p>

            {search && (
              <p className="mt-1 text-[10px] text-[#a4adb3]">
                Try changing your search or filter.
              </p>
            )}
          </div>
        )}
      </div>

      <Modal
  open={isRentDistributorOpen}
  onCancel={handleCloseRentDistributor}
  footer={null}
  centered
  width={460}
  destroyOnHidden
>
  <div className="px-1 py-2">
    <h2 className="text-[18px] font-semibold text-[#183542]">
      Distribute Rent
    </h2>

    <p className="mt-1 text-[10px] text-[#8a9295]">
      Enter the rent amount and select the month for distribution.
    </p>

    <div className="mt-6 space-y-5">

      {/* Amount */}
      <div>
        <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-[#77818c]">
          Amount
          <span className="ml-1 text-[#4c7a2e]">*</span>
        </label>

        <Input
          type="number"
          value={rentAmount}
          onChange={(e) => setRentAmount(e.target.value)}
          placeholder="Enter amount"
          size="large"
          min={0}
        />
      </div>

    <div>
  <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-[#77818c]">
    Month
    <span className="ml-1 text-[#4c7a2e]">*</span>
  </label>

  <DatePicker
    picker="month"
    value={selectedMonth}
    onChange={(date) => setSelectedMonth(date)}
    placeholder="Select month"
    size="large"
    format="MMMM YYYY"
    className="w-full"
    allowClear
  />
</div>
      {/* Buttons */}
      <div className="flex justify-end gap-2 border-t border-[#e7e7e4] pt-5">

        <button
          type="button"
          onClick={handleCloseRentDistributor}
          className="
            h-[40px]
            rounded-[8px]
            border
            border-[#d9ddd8]
            bg-[#f8f8f5]
            px-5
            text-[12px]
            font-semibold
            text-[#59656a]
            transition
            hover:bg-[#f1f2ee]
          "
        >
          Cancel
        </button>

        <button
  type="button"
  onClick={handleRentDistributionSubmit}
  disabled={
    !rentAmount ||
    !selectedMonth ||
    isRentSubmitting
  }
  className="
    h-[40px] rounded-[8px] bg-[#4c7a2e]
    px-5 text-[12px] font-semibold text-white
    transition hover:bg-[#375c20]
    disabled:cursor-not-allowed disabled:opacity-50
  "
>
  {isRentSubmitting ? "Distributing..." : "Distribute Rent"}
</button>

      </div>
    </div>
  </div>
</Modal>

<Modal
  open={isApproveModalOpen}
  onCancel={handleCloseApproveModal}
  footer={null}
  centered
  width={460}
  destroyOnHidden
>
  <div className="px-1 py-2">
    <h2 className="text-[18px] font-semibold text-[#183542]">
      Approve &amp; Tokenize Property
    </h2>

    <p className="mt-1 text-[10px] text-[#8a9295]">
      This creates the on-chain token for{" "}
      <span className="font-semibold">{propertyToApprove?.name}</span> and
      mints its full supply. Set a vesting period if newly-acquired tokens
      should be locked before investors can resell or transfer them.
    </p>

    <div className="mt-6 space-y-5">
      {/* Vesting days */}
      <div>
        <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.08em] text-[#77818c]">
          Vesting Period (days)
        </label>

        <Input
          type="number"
          value={vestingDays}
          onChange={(e) => setVestingDays(e.target.value)}
          placeholder="0"
          size="large"
          min={0}
        />

        <p className="mt-1.5 text-[10px] text-[#a4adb3]">
          0 = no vesting — tokens are transferable immediately after
          purchase. Any positive number locks newly-acquired tokens on a
          linear release schedule over that many days. Pre-filled from what
          the owner requested at submission, if anything — you can still
          change it here before tokenizing. This cannot be changed once the
          property is tokenized.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 border-t border-[#e7e7e4] pt-5">
        <button
          type="button"
          onClick={handleCloseApproveModal}
          disabled={isApproving}
          className="
            h-[40px]
            rounded-[8px]
            border
            border-[#d9ddd8]
            bg-[#f8f8f5]
            px-5
            text-[12px]
            font-semibold
            text-[#59656a]
            transition
            hover:bg-[#f1f2ee]
            disabled:cursor-not-allowed
            disabled:opacity-50
          "
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleApproveSubmit}
          disabled={isApproving}
          className="
            h-[40px] rounded-[8px] bg-[#4c7a2e]
            px-5 text-[12px] font-semibold text-white
            transition hover:bg-[#375c20]
            disabled:cursor-not-allowed disabled:opacity-50
          "
        >
          {isApproving ? "Tokenizing..." : "Approve & Tokenize"}
        </button>
      </div>
    </div>
  </div>
</Modal>
    </AdminLayout>
  );
};

/* ================= STATUS BADGE ================= */
const IsPublished = ({ status }) => {
  const styles = {
    Pending: "bg-[#fff1c7] text-[#bd7b00]",
    Approved: "bg-[#d9f8e4] text-[#159447]",
    Rejected: "bg-[#ffe0e0] text-[#df3d3d]",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        styles[status] || "bg-[#f3f5f7] text-[#71808c]"
      }`}
    >
      {status === "Pending" ? "UnPublished" : "Published"}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    Pending: "bg-[#fff1c7] text-[#bd7b00]",
    Approved: "bg-[#d9f8e4] text-[#159447]",
    Rejected: "bg-[#ffe0e0] text-[#df3d3d]",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        styles[status] || "bg-[#f3f5f7] text-[#71808c]"
      }`}
    >
      {status}
    </span>
  );
};

export default AdminProperties;